import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { parsePhoneNumber } from 'libphonenumber-js';
import { getI18n } from 'react-i18next';

import V4API, {
	getV4DeviceUuid,
	getV4GeofenceUuid,
	getV4TeamUuid,
	v4ScopeFor,
} from '../../api/V4client';
import {
	FETCH_V4_SCENARIOS,
	REFRESH_V4_SCENARIO_LAST_TRIGGERED,
	SCENARIOS,
	SCENARIOS_CHANGED,
	WS_DATA_SCENARIOS,
} from '../constants/scenario.constants';
import { WS_DATA_INIT } from '../constants/app.constants';
import { WS_DATA_DEVICES } from '../constants/device.constants';
import { selectDevices } from '../selectors/device.selector';
import { selectTeams } from '../selectors/team.selector';
import { selectGeofences } from '../selectors/geofence.selector';
import { selectContacts } from '../selectors/contact.selector';
import { formatPhoneToPlain, isValidPhoneNumber } from '../../utils/functions';
import {
	fetchV4Scenarios as fetchV4ScenariosAction,
	v4ScenarioDedupUpdated,
	v4ScenarioLastTriggeredUpdated,
	v4ScenariosLoaded,
} from '../actions/scenario.actions';
import { openSnackbar } from '../actions/snackbar.actions';
import { selectCurrentUser } from '../selectors/app.selectors';
import {
	deleteV4Scenario,
	executeV4Scenario,
	fetchV4Scenario,
	fetchV4Scenarios,
	getReferencedV3ActionIds,
	getReferencedV3DeviceIds,
	getUnresolvedV3Recipients,
	saveV4Scenario,
	testV4ScenarioAction,
	toV4ScenarioRequest,
	toV4TestActions,
	v4ActionSources,
	v4SaveErrors,
} from '../../containers/V4Scenarios/scenarios';
import { loadV4ScenarioDictionaries } from '../../containers/V4Scenarios/dictionaries';
import { ensureV4DeviceUuid, lookupOrCreateDeviceV4 } from './deviceV4.saga';
import { V4_NATIVE_ACCOUNT } from '../../utils/V4AlarmWebSocket';
import { formatApiError } from '../../utils/v4ApiError';

// V3→V4 scenario id mapping is stable for the page lifetime; cache hits and
// misses so V3 refreshes don't re-issue every lookup.
const scenarioMigrationCache = new Map();

// uuid -> v3 id from save-time device resolutions; lets a just-linked device
// round-trip before its uuid surfaces in the device list.
const deviceUuidToV3IdCache = new Map();

// Same for teams and geofences, which action configs now address by uuid. No
// bulk V3->V4 mapping exists for either, so this only covers what this session
// resolved; an unmapped uuid passes through, as it does for triggers today.
const teamUuidToV3IdCache = new Map();
const geofenceUuidToV3IdCache = new Map();

const inverseOf = cache => Object.fromEntries(cache);

const v4DeviceImeiPairsByAccount = new Map();

async function fetchV4DeviceImeiPairs(accountId, scope) {
	if (v4DeviceImeiPairsByAccount.has(accountId)) {
		return v4DeviceImeiPairsByAccount.get(accountId);
	}
	let pairs = [];
	try {
		const res = await V4API.devicesList(scope);
		pairs = (res?.data || [])
			.map(d => ({ uuid: d.id, imei: d.metadata?.imei || d.metadata?.unique_id || null }))
			.filter(p => p.uuid && p.imei);
	} catch (e) { /* non-fatal — other layers below still apply */ }
	v4DeviceImeiPairsByAccount.set(accountId, pairs);
	return pairs;
}

// v4 uuid -> v3 id. V3 records don't reliably carry their uuid on a cold load,
// so join the V4 device list to v3 on IMEI; save-time cache wins.
function* buildDeviceUuidToV3IdMap(accountId, scope) {
	const devices = yield select(selectDevices());
	const list = devices?.toArray ? devices.toArray() : devices || [];
	const map = {};
	const imeiToV3Id = {};
	list.forEach(d => {
		const id = d?.get?.('id') ?? d?.id;
		const uuid = d?.get?.('uuid') ?? d?.uuid;
		const imei = d?.get?.('uniqueId') ?? d?.uniqueId;
		if (uuid != null) map[uuid] = id;
		if (imei) imeiToV3Id[String(imei)] = id;
	});

	const v4Pairs = yield call(fetchV4DeviceImeiPairs, accountId, scope);
	v4Pairs.forEach(({ uuid, imei }) => {
		const v3Id = imeiToV3Id[String(imei)];
		if (v3Id != null) map[uuid] = v3Id;
	});

	deviceUuidToV3IdCache.forEach((v3Id, uuid) => { map[uuid] = v3Id; });
	return map;
}

async function lookupV4UuidForV3Scenario(v3Id) {
	if (scenarioMigrationCache.has(v3Id)) return scenarioMigrationCache.get(v3Id);
	try {
		const res = await V4API.migrationLookupDetail(`scenario_${v3Id}`);
		const uuid = res?.data?.uuid || null;
		scenarioMigrationCache.set(v3Id, uuid);
		return uuid;
	} catch (e) {
		scenarioMigrationCache.set(v3Id, null);
		return null;
	}
}

function* resolveV4ScopeForCurrentUser() {
	const user = yield select(selectCurrentUser());
	if (!user?.accountId) return null;
	try {
		return yield call(() => v4ScopeFor(user.accountId));
	} catch (e) {
		return null;
	}
}

function* selectV3ScenarioIds() {
	const data = yield select(state =>
		state[SCENARIOS]?.get?.('data')?.toList?.() || null
	);
	const list = data?.toArray ? data.toArray() : [];
	return list
		.map(s => s?.get?.('id') ?? s?.id)
		.filter(id => id != null && id !== 0);
}

// V3 scenarios — unlike V3 devices — don't carry their V4 UUID on the wire,
// so use the explicit migrationLookup endpoint to find replicated ones.
function* resolveV3ToV4ScenarioUuids() {
	const v3Ids = yield* selectV3ScenarioIds();
	if (!v3Ids.length) return [];
	const lookups = v3Ids.map(id => call(lookupV4UuidForV3Scenario, id));
	const uuids = yield all(lookups);
	return uuids.filter(Boolean).map(String);
}

export function* fetchV4ScenariosForUserSaga() {
	try {
		const user = yield select(selectCurrentUser());
		if (!user || (!user.accountId && !user.isV4Native)) {
			yield put(v4ScenariosLoaded([], []));
			return;
		}

		// V4-native: V4client supplies the scope, and their devices already carry
		// V4 uuids as ids.
		let v4Scope = null;
		// uuid -> v3 id; empty for V4-native, where the uuids ARE the ids.
		let deviceUuidToV3Id = {};
		if (!user.isV4Native) {
			v4Scope = yield* resolveV4ScopeForCurrentUser();
			if (!v4Scope) {
				yield put(v4ScenariosLoaded([], []));
				return;
			}
			deviceUuidToV3Id = yield* buildDeviceUuidToV3IdMap(user.accountId, v4Scope);
		}
		const contactAddresses = yield* resolveContactAddresses();
		const [v4Scenarios, dedupUuids] = yield all([
			// `timezone` decodes the UTC schedule masks back to the local grid.
			call(fetchV4Scenarios, v4Scope, {
				idMaps: {
					deviceUuidToV3Id,
					teamUuidToV3Id: inverseOf(teamUuidToV3IdCache),
					geofenceUuidToV3Id: inverseOf(geofenceUuidToV3IdCache),
					...contactAddresses,
				},
				timezone: user.timezone,
			}),
			call(resolveV3ToV4ScenarioUuids),
			call(loadV4ScenarioDictionaries, v4Scope),
		]);

		yield put(v4ScenariosLoaded(v4Scenarios, dedupUuids));
	} catch (e) {
		// eslint-disable-next-line no-console
		console.warn('Failed to fetch V4 scenarios for user:', e);
		yield put(v4ScenariosLoaded([], []));
	}
}

// A V4-native session's only device list is the seed's, just before WS_DATA_INIT
// loads the scenarios, and its uuids need no mapping back.
export function* refetchV4ScenariosForDevicesSaga() {
	const user = yield select(selectCurrentUser());
	if (user?.isV4Native) return;
	yield* fetchV4ScenariosForUserSaga();
}

// The action-triggered websocket topic identifies one scenario. Re-read only
// that scenario so Last run updates without replacing the whole V4 list.
export function* refreshV4ScenarioLastTriggeredSaga({ scenarioId, accountId }) {
	if (!scenarioId) return;
	try {
		const user = yield select(selectCurrentUser());
		let scope;
		if (accountId && accountId !== V4_NATIVE_ACCOUNT) {
			scope = yield call(() => v4ScopeFor(accountId));
		} else if (!accountId && !user?.isV4Native) {
			scope = yield* resolveV4ScopeForCurrentUser();
			if (!scope) return;
		}

		const scenario = yield call(fetchV4Scenario, scenarioId, scope);
		if (!scenario || String(scenario.id) !== String(scenarioId)) return;
		yield put(
			v4ScenarioLastTriggeredUpdated(scenarioId, scenario.last_triggered_at)
		);
	} catch (e) {
		// Keep the current timestamp on a transient failure; the next trigger (or
		// regular scenario load) will reconcile it.
		// eslint-disable-next-line no-console
		console.warn(`Failed to refresh V4 scenario ${scenarioId}:`, e);
	}
}

// Refresh just the dedup set (no V4 refetch) when V3 changes.
export function* refreshScenarioDedupSaga() {
	try {
		const dedupUuids = yield* resolveV3ToV4ScenarioUuids();
		yield put(v4ScenarioDedupUpdated(dedupUuids));
	} catch (e) {
		// non-fatal — keep whatever dedup state we had
	}
}

// Resolve a batch of V3 ids, dropping those with no V4 counterpart; one failure
// must not fail the batch.
function* resolveIdMap(v3Ids, resolve) {
	const map = {};
	if (!v3Ids.length) return map;
	const results = yield all(
		v3Ids.map(id =>
			call(function* () {
				try {
					return { id, uuid: yield call(resolve, id) };
				} catch (e) {
					return { id, uuid: null };
				}
			})
		)
	);
	results.forEach(({ id, uuid }) => {
		if (uuid) map[id] = uuid;
	});
	return map;
}

// Resolves the V3 ids referenced by triggerConfig AND by the action configs
// (recipients, change-team targets, follow-up scenario) to V4 uuids. Tests
// skip the trigger, whose lookup creates V4 twins on a miss.
export function* resolveScenarioIdMaps(scenario, scope, { withTrigger = true } = {}) {
	const triggerDeviceIds = withTrigger ? getReferencedV3DeviceIds(scenario) : [];
	const action = getReferencedV3ActionIds(scenario);
	// A device the trigger already covers keeps the trigger's resolution.
	const actionOnlyDeviceIds = action.deviceIds.filter(
		id => triggerDeviceIds.indexOf(id) === -1
	);
	const devices = actionOnlyDeviceIds.length ? yield select(selectDevices()) : null;

	const [triggerDevices, actionDevices, teamIdToUuid, geofenceIdToUuid, scenarioIdToUuid] =
		yield all([
			// Creates the V4 twin on a miss, as the trigger has always done.
			call(resolveIdMap, triggerDeviceIds, function* (id) {
				return yield call(lookupOrCreateDeviceV4, id);
			}),
			// Migrated on a miss too, behind the ARC link's type gate.
			call(resolveIdMap, actionOnlyDeviceIds, function* (id) {
				const device = devices?.find?.(d => +d.id === id);
				// No V3 record to create from.
				if (!device) return yield call(getV4DeviceUuid, id);
				return yield call(ensureV4DeviceUuid, device);
			}),
			call(resolveIdMap, action.teamIds, id => getV4TeamUuid(id, scope)),
			call(resolveIdMap, action.geofenceIds, id => getV4GeofenceUuid(id, scope)),
			call(resolveIdMap, action.scenarioIds, lookupV4UuidForV3Scenario),
		]);

	const deviceIdToUuid = { ...actionDevices, ...triggerDevices };
	// Remember the inverse so the next load shows the pickers a row, not a uuid.
	const remember = (map, cache) =>
		Object.entries(map).forEach(([id, uuid]) => cache.set(uuid, +id));
	remember(deviceIdToUuid, deviceUuidToV3IdCache);
	remember(teamIdToUuid, teamUuidToV3IdCache);
	remember(geofenceIdToUuid, geofenceUuidToV3IdCache);
	return { deviceIdToUuid, teamIdToUuid, geofenceIdToUuid, scenarioIdToUuid };
}

// translateIds would silently drop these recipients: throw, naming them (also on
// `error.unresolvedRecipients`).
export function* refuseUnresolvedRecipients(scenario, idMaps) {
	const missing = getUnresolvedV3Recipients(scenario, idMaps);
	const names = [];
	for (const [ids, selectEntities] of [
		[missing.devices, selectDevices],
		[missing.teams, selectTeams],
		[missing.geofences, selectGeofences],
	]) {
		if (!ids.length) continue;
		const entities = yield select(selectEntities());
		names.push(...ids.map(id => entities?.find?.(e => +e.id === id)?.name || `#${id}`));
	}
	if (!names.length) return;
	const error = new Error(`these recipients are not in V4: ${names.join(', ')}`);
	error.unresolvedRecipients = names;
	throw error;
}

// As the sms and voice editors normalise a typed number.
function toE164(phone) {
	const plain = formatPhoneToPlain(phone || '', true);
	if (!isValidPhoneNumber(plain)) return null;
	try {
		return parsePhoneNumber(plain).number;
	} catch (e) {
		return null;
	}
}

// V4 takes only an e-mail address or an E.164 number as a recipient, so a
// picked contact (`{{c-<id>}}`) is sent as its address and read back by it.
function* resolveContactAddresses() {
	const contacts = yield select(selectContacts());
	const contactIdToEmail = {};
	const contactIdToPhone = {};
	const emailToContactId = {};
	const phoneToContactId = {};
	contacts.forEach(contact => {
		const email = contact.email?.toLowerCase();
		const phone = toE164(contact.phone);
		if (email) {
			contactIdToEmail[contact.id] = contact.email;
			if (!(email in emailToContactId)) emailToContactId[email] = contact.id;
		}
		if (phone) {
			contactIdToPhone[contact.id] = phone;
			if (!(phone in phoneToContactId)) phoneToContactId[phone] = contact.id;
		}
	});
	return { contactIdToEmail, contactIdToPhone, emailToContactId, phoneToContactId };
}

export function* saveV4ScenarioSaga(scenario, { showsMarks = false } = {}) {
	let body = null;
	let idMaps = null;
	let sources = null;
	try {
		// V4-native has no V3 account to resolve — V4client supplies the scope.
		const user = yield select(selectCurrentUser());
		const v4Scope = yield* resolveV4ScopeForCurrentUser();
		if (!v4Scope && !user?.isV4Native) {
			yield put(openSnackbar('Could not save V4 scenario: account not migrated to V4'));
			return { error: new Error('account not migrated to V4'), actionErrors: {} };
		}
		idMaps = {
			...(yield* resolveScenarioIdMaps(scenario, v4Scope)),
			...(yield* resolveContactAddresses()),
		};
		yield* refuseUnresolvedRecipients(scenario, idMaps);
		body = toV4ScenarioRequest(scenario, { idMaps, timezone: user?.timezone });
		sources = v4ActionSources(scenario, { idMaps });
		yield call(saveV4Scenario, scenario, body, v4Scope);
		yield put(fetchV4ScenariosAction());
		return null;
	} catch (e) {
		const marks = v4SaveErrors(e, scenario, { body, sources, idMaps });
		const isMarked = showsMarks && (!!marks.nameError || Object.keys(marks.actionErrors).length > 0);
		yield put(openSnackbar(
			isMarked
				? getI18n().t('scenarioNotSaved.fixMarkedFields', 'Scenario not saved: fix the marked fields')
				: formatApiError('save', 'V4 scenario', e)
		));
		// eslint-disable-next-line no-console
		console.warn('[V4 scenario save failed]', {
			status: e?.response?.status,
			response: e?.response?.data,
			sentBody: body,
		});
		return { error: e, ...marks };
	}
}

export function* testV4ScenarioActionSaga(scenario, actionIndex) {
	let actions = null;
	try {
		const user = yield select(selectCurrentUser());
		const v4Scope = yield* resolveV4ScopeForCurrentUser();
		if (!v4Scope && !user?.isV4Native) {
			yield put(openSnackbar('Could not test V4 scenario action: account not migrated to V4'));
			return;
		}
		const tested = scenario.set('actions', [scenario.get('actions')[actionIndex]]);
		const idMaps = {
			...(yield* resolveScenarioIdMaps(tested, v4Scope, { withTrigger: false })),
			...(yield* resolveContactAddresses()),
		};
		yield* refuseUnresolvedRecipients(tested, idMaps);
		actions = toV4TestActions(scenario, actionIndex, { idMaps });
		for (const action of actions) {
			yield call(testV4ScenarioAction, scenario, action, v4Scope);
		}
	} catch (e) {
		yield put(openSnackbar(formatApiError('test', 'V4 scenario action', e)));
		// eslint-disable-next-line no-console
		console.warn('[V4 scenario action test failed]', {
			status: e?.response?.status,
			response: e?.response?.data,
			sentActions: actions,
		});
	}
}

export function* deleteV4ScenarioSaga(scenario) {
	try {
		const user = yield select(selectCurrentUser());
		const v4Scope = yield* resolveV4ScopeForCurrentUser();
		if (!v4Scope && !user?.isV4Native) {
			yield put(openSnackbar('Could not delete V4 scenario: account not migrated to V4'));
			return;
		}
		yield call(deleteV4Scenario, scenario, v4Scope);
		yield put(fetchV4ScenariosAction());
	} catch (e) {
		yield put(openSnackbar(formatApiError('delete', 'V4 scenario', e)));
		// eslint-disable-next-line no-console
		console.warn('[V4 scenario delete failed]', {
			status: e?.response?.status,
			response: e?.response?.data,
		});
	}
}

function* executeV4ScenarioSaga(scenarioId, scope) {
	try {
		yield call(executeV4Scenario, scenarioId, scope);
	} catch (e) {
		yield put(openSnackbar(formatApiError('start', 'V4 scenario', e)));
		// eslint-disable-next-line no-console
		console.warn('[V4 scenario start failed]', {
			scenarioId,
			status: e?.response?.status,
			response: e?.response?.data,
		});
	}
}

// No batch start in V4. The tile already toasts success.
export function* startV4ScenariosSaga(scenarioIds) {
	const user = yield select(selectCurrentUser());
	const v4Scope = yield* resolveV4ScopeForCurrentUser();
	if (!v4Scope && !user?.isV4Native) {
		yield put(openSnackbar('Could not start V4 scenario: account not migrated to V4'));
		return;
	}
	yield all(scenarioIds.map(id => call(executeV4ScenarioSaga, id, v4Scope)));
}

export const scenarioV4Sagas = [
	takeLatest(FETCH_V4_SCENARIOS, fetchV4ScenariosForUserSaga),
	takeEvery(REFRESH_V4_SCENARIO_LAST_TRIGGERED, refreshV4ScenarioLastTriggeredSaga),
	takeLatest(WS_DATA_INIT, fetchV4ScenariosForUserSaga),
	// Refire once the device list (re)loads so device_id UUIDs map back to v3 ids.
	takeLatest(WS_DATA_DEVICES, refetchV4ScenariosForDevicesSaga),
	takeLatest(WS_DATA_SCENARIOS, refreshScenarioDedupSaga),
	takeLatest(SCENARIOS_CHANGED, refreshScenarioDedupSaga),
];
